import { Observable } from "rxjs";
import { Option } from "../option.model";
import { DynamicField } from "./dynamic-field.model";
import { FilterOn } from "../filter-on.model";

export class DynamicSelect<U, T> extends DynamicField<T> {

    multiSelect?: boolean;
    source?: Array<U>;
    displayValue!: keyof U;
    nameValue!: keyof U;
    showClear?: boolean;
    options!: Array<Option>;
    filter?: boolean;
    sourceObserver?: Observable<Array<U>>;
    filterObserver?: Observable<number>;
    filterOn?: FilterOn;

    constructor(source: Partial<DynamicSelect<U, T>>) {
        super();
        this.showClear = true;
        this.valuesFilter = [];
        this.appendTo = 'body';
        Object.assign(this, source);

        this.sourceObserver?.subscribe(data => {
            this.source = data;
            this.options = this.createOptions();
        });

        if (this.source) {
            this.options = this.createOptions();
        }
    }

    /**
     * The function sets an observer for filtering data and updates the options based on the filtered
     * data.
     * @param observer - The observer parameter is an instance of the Observable class from a reactive
     * programming library. It is used to subscribe to changes in the filter value.
     */
    setFilterObserver(observer: Observable<number>) {
        this.filterObserver = observer;

        this.filterObserver.subscribe(filter => {
            let data = this.source?.filter((item) => item[this.filterOn?.filterSourceProperty as keyof U] === filter);
            this.options = this.createOptionsFilter(data!);
        });
    }

    /**
     * The function creates an array of Option objects based on the values in the source array.
     * @returns The function `createOptions()` returns an array of `Option` objects.
     */
    createOptions(): Array<Option> {
        let options: Array<Option> | undefined = [];

        options = this.source?.map(item => {
            return new Option({
                id: this.isNumeric(item[this.nameValue] as string) ? parseInt(item[this.nameValue] as string) : item[this.nameValue] as string,
                name: item[this.displayValue] as string
            });
        });

        return options!;
    }

    /**
     * The function `createOptionsFilter` takes an array of objects and creates an array of `Option`
     * objects based on the specified properties.
     * @param source - An array of objects of type U, which is the source data for creating the
     * options.
     * @returns The function `createOptionsFilter` returns an array of `Option` objects.
     */
    createOptionsFilter(source: Array<U>): Array<Option> {
        let options: Array<Option> | undefined = [];

        options = source.map(item => {
            return new Option({
                id: this.isNumeric(item[this.nameValue] as string) ? parseInt(item[this.nameValue] as string) : item[this.nameValue] as string,
                name: item[this.displayValue] as string
            });
        });

        return options!;
    }

    private isNumeric = (val: string): boolean => {
        return !isNaN(Number(val));
    }

}